import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InvoiceLine } from '../api';

interface Props {
    issueDate: string;
    invoiceType: string;
    patientName: string;
    lines: InvoiceLine[];
    subtotal: number;
    total: number;
}

const InvoicePreview: React.FC<Props> = ({ issueDate, invoiceType, patientName, lines, subtotal, total }) => {
    const { t } = useTranslation();
    return (
        <div data-ui="invoice-preview-region-001" className="bg-[#11162d] p-6 rounded-2xl border border-gray-800 shadow-xl flex justify-center overflow-auto">
            <div data-ui="invoice-preview-region-002" className="bg-white w-full max-w-[210mm] min-h-[297mm] p-12 text-black shadow-lg">
                {/* Header */}
                <div data-ui="invoice-preview-region-003" className="flex justify-between items-start border-b border-gray-300 pb-6 mb-6">
                    <div data-ui="invoice-preview-region-004">
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight">{t('invoices.preview.title')}</h1>
                        <div data-ui="invoice-preview-region-005" className="text-sm font-semibold text-gray-500 mt-2">{t('invoices.preview.draftSubtitle')}</div>
                    </div>
                    <div data-ui="invoice-preview-region-006" className="text-right">
                        <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-full text-xs font-bold mb-2">{t('invoices.dashboard.status.draft')}</span>
                        <div data-ui="invoice-preview-region-007" className="text-sm text-gray-600">{t('invoices.preview.date')}: {new Date(issueDate).toLocaleDateString()}</div>
                        <div data-ui="invoice-preview-region-008" className="text-sm text-gray-600">{t('invoices.preview.type')}: {invoiceType}</div>
                    </div>
                </div>

                {/* Client Info */}
                <div data-ui="invoice-preview-region-009" className="mb-8">
                    <div data-ui="invoice-preview-region-010" className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('invoices.preview.billTo')}</div>
                    <div data-ui="invoice-preview-region-011" className="text-lg font-bold text-gray-800">{patientName}</div>
                </div>

                {/* Lines Table */}
                <table className="w-full text-left text-sm mb-8">
                    <thead>
                        <tr className="border-b-2 border-gray-900">
                            <th className="py-3 font-bold text-gray-700">{t('invoices.preview.table.concept')}</th>
                            <th className="py-3 font-bold text-gray-700 text-center">{t('invoices.preview.table.quantity')}</th>
                            <th className="py-3 font-bold text-gray-700 text-right">{t('invoices.preview.table.price')}</th>
                            <th className="py-3 font-bold text-gray-700 text-right">{t('invoices.preview.table.tax')}</th>
                            <th className="py-3 font-bold text-gray-700 text-right">{t('invoices.preview.table.total')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 border-b border-gray-300">
                        {lines.map((l, i) => (
                            <tr key={i}>
                                <td className="py-4 text-gray-800">{l.concept_name}</td>
                                <td className="py-4 text-gray-800 text-center">{l.quantity}</td>
                                <td className="py-4 text-gray-800 text-right">{l.unit_price.toFixed(2)} €</td>
                                <td className="py-4 text-gray-500 text-right text-xs">IGIC {l.tax_rate}%</td>
                                <td className="py-4 text-gray-900 font-bold text-right">{l.total.toFixed(2)} €</td>
                            </tr>
                        ))}
                        {lines.length === 0 && (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-gray-400 italic">{t('invoices.preview.table.noConcepts')}</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Totals */}
                <div data-ui="invoice-preview-region-012" className="flex justify-end mb-16">
                    <div data-ui="invoice-preview-region-013" className="w-64">
                        <div data-ui="invoice-preview-region-014" className="flex justify-between py-2 border-b border-gray-200">
                            <span className="font-semibold text-gray-600">{t('invoices.preview.subtotal')}</span>
                            <span className="text-gray-800">{subtotal.toFixed(2)} €</span>
                        </div>
                        <div data-ui="invoice-preview-region-015" className="flex justify-between py-3 border-b-2 border-gray-900">
                            <span className="font-bold text-lg text-gray-900">{t('invoices.preview.total')}</span>
                            <span className="font-bold text-lg text-gray-900">{total.toFixed(2)} €</span>
                        </div>
                    </div>
                </div>

                {/* VeriFactu Footer placeholder */}
                <div data-ui="invoice-preview-region-016" className="flex justify-between items-end mt-auto pt-16">
                    <div data-ui="invoice-preview-region-017">
                        <div data-ui="invoice-preview-region-018" className="text-xl font-black tracking-widest text-gray-900">VERI*FACTU</div>
                        <div data-ui="invoice-preview-region-019" className="text-xs italic text-gray-500">{t('invoices.preview.verifactuMessage')}</div>
                    </div>
                    <div data-ui="invoice-preview-region-020" className="w-24 h-24 border border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                        <span className="text-xs text-center text-gray-400 px-2">{t('invoices.preview.qrPlaceholder').split('\n').map((line, i) => <React.Fragment key={i}>{line}<br/></React.Fragment>)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoicePreview;
